// @ts-ignore
import zoid from '@krakenjs/zoid/dist/zoid.frameworks';
import {ComponentOptionsType, MoneiComponent} from '../../types/component';
import {createApplePaySession, createToken, getPaymentMethods} from '../api';
import {config} from '../config';
import {containerTemplate} from '../container';
import {validateComponentProps} from '../utils';
import {PaymentRequestProps} from './types';

export * from './types';

type Context = {
  accountId: string;
  domainName: string;
  displayName: string;
};

const onApplePayButtonClicked = async (props: PaymentRequestProps, request: any, context: Context) => {
  const session = new window.ApplePaySession(3, request);
  session.onvalidatemerchant = async (event: any) => {
    try {
      const merchantSession = await createApplePaySession({
        ...context,
        validationUrl: event.validationURL
      });
      session.completeMerchantValidation(merchantSession);
    } catch (error) {
      session.abort();
    }
  };

  session.onpaymentauthorized = async (event: any) => {
    const paymentToken = JSON.stringify(event.payment.token);
    const token = window.btoa(paymentToken);
    try {
      const result = await createToken({
        accountId: props.accountId,
        paymentId: props.paymentId,
        sessionId: props.sessionId,
        paymentMethod: {
          applePay: {token}
        }
      });
      props.onSubmit({token: result.paymentToken, error: result.error});
      session.completePayment({
        status: window.ApplePaySession.STATUS_SUCCESS
      });
    } catch (error) {
      props.onError?.(error);
      session.completePayment({
        status: window.ApplePaySession.STATUS_FAILURE
      });
    }
  };
  session.begin();
};

const options: ComponentOptionsType<PaymentRequestProps> = {
  tag: 'monei-payment-request',
  url: config.paymentRequestUrl,
  dimensions: {
    width: '100%',
    height: 'auto'
  },
  autoResize: {
    width: false,
    height: true
  },
  validate: validateComponentProps,
  containerTemplate,
  prerenderTemplate: () => undefined,
  props: {
    accountId: {
      type: zoid.PROP_TYPE.STRING,
      required: false
    },
    sessionId: {
      type: zoid.PROP_TYPE.STRING,
      required: false
    },
    paymentId: {
      type: zoid.PROP_TYPE.STRING,
      required: false
    },
    amount: {
      type: zoid.PROP_TYPE.NUMBER,
      required: false
    },
    currency: {
      type: zoid.PROP_TYPE.STRING,
      required: false
    },
    language: {
      type: zoid.PROP_TYPE.STRING,
      required: false
    },
    onError: {
      type: zoid.PROP_TYPE.FUNCTION,
      required: false
    },
    onLoad: {
      type: zoid.PROP_TYPE.FUNCTION,
      required: false
    },
    onBeforeOpen: {
      type: zoid.PROP_TYPE.FUNCTION,
      required: false
    },
    onSubmit: {
      type: zoid.PROP_TYPE.FUNCTION,
      required: false
    },
    onBeforeSubmit: {
      type: zoid.PROP_TYPE.FUNCTION,
      required: false
    },
    style: {
      type: zoid.PROP_TYPE.OBJECT,
      alias: 'innerStyle',
      required: false
    },
    __getConfig: {
      type: zoid.PROP_TYPE.FUNCTION,
      required: false,
      value: ({props}: any) => {
        return getPaymentMethods.bind(null, props);
      }
    },
    __isApplePaySupported: {
      type: zoid.PROP_TYPE.BOOLEAN,
      required: false,
      value: () => {
        try {
          return window.ApplePaySession?.canMakePayments();
        } catch (error) {
          return false;
        }
      }
    },
    __onApplePayButtonClicked: {
      type: zoid.PROP_TYPE.FUNCTION,
      required: false,
      value({props}: {props: PaymentRequestProps}) {
        return onApplePayButtonClicked.bind(null, props);
      }
    }
  }
};

export const PaymentRequest: MoneiComponent<PaymentRequestProps> = zoid.create(options);
